import {
  trackFlowComplete,
  trackFlowStep,
  trackFeatureUsage,
  type ConversionSurface,
} from "@/utils/gtm";
import type { DemoStepId } from "./flows";

/**
 * This run's identity in the shared flow_step / flow_complete events.
 *
 * One pair of events covers both guided runs; flow_name is what separates the
 * logged-out demo from the logged-in onboarding rail.
 */
const DEMO_FLOW_NAME = "demo";
const DEMO_SURFACE: ConversionSurface = "demo_guide";

/**
 * Demo-guide funnel events.
 *
 * The question these exist to answer is which of two problems we have: nobody
 * discovers the value, or they discover it and still won't pay for the trial.
 * Those need different fixes, and without the middle of the funnel between
 * `demo_view` and `trial_cta_click` there is no way to tell them apart.
 *
 * Two tiers, deliberately:
 *
 *   The run's progress — a step completed, the run finished — goes out as the
 *   shared `flow_step` / `flow_complete` events below, because that is the
 *   series anyone actually charts and it should not have to be reassembled
 *   from a generic event's free-text detail field.
 *
 *   Everything around the run — the picker, the checklist, exits, restarts —
 *   stays on the existing GA4 feature_usage event, so it lands in the same
 *   container with user props attached and costs no new GTM tags. This matches
 *   src/onboarding/analytics.ts.
 */
export type DemoGuideAnalyticsAction =
  | "picker_shown"
  | "picker_dismissed"
  | "asset_class_picked"
  // A skipped step is no longer reported here. It rides on flow_step with
  // `skipped: true`, so the step stream stays unbroken and a funnel can be read
  // from one series — see trackFlowStep.
  | "run_exited"
  | "checklist_opened"
  | "checklist_dismissed"
  | "watchlist_created"
  | "restarted";

export function trackDemoGuide(
  action: DemoGuideAnalyticsAction,
  detail?: string
) {
  trackFeatureUsage({
    feature_name: "demo_guide",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * One step reached its end, however it ended.
 *
 * Emitted for skipped steps too — see trackFlowStep for why the step stream has
 * to be unbroken for a funnel to be readable.
 */
export function trackDemoGuideStep(params: {
  stepId: DemoStepId;
  /** Zero-based position in the run; converted to 1-based for the event. */
  stepIndex: number;
  stepTotal: number;
  skipped: boolean;
}) {
  trackFlowStep({
    flow_name: DEMO_FLOW_NAME,
    step_id: params.stepId,
    step_index: params.stepIndex + 1,
    step_total: params.stepTotal,
    skipped: params.skipped,
    surface: DEMO_SURFACE,
    authenticated: false,
  });
}

/** The run reached the end. Once per run, in addition to the final step. */
export function trackDemoGuideComplete(params: {
  stepsCompleted: number;
}) {
  trackFlowComplete({
    flow_name: DEMO_FLOW_NAME,
    steps_completed: params.stepsCompleted,
    surface: DEMO_SURFACE,
    authenticated: false,
  });
}
